import { motion, useScroll, useTransform } from "framer-motion";
import { useState, useEffect, useRef } from "react";
import podImage from "@/assets/pod-design.png";

const answers = [
  "hop on a teams call?",
  "call your doctor?",
  "attend a job interview?",
  "find a moment of calm?",
  "focus for 30 minutes?"
];

const StickyQuestion = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  
  // 1. Track Scroll Progress
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end center"]
  });

  const { scrollYProgress: entryProgress } = useScroll({
    target: containerRef,
    offset: ["start 90%", "start 10%"]
  });

  // 2. Cycling Answers Logic
  const [currentAnswer, setCurrentAnswer] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentAnswer((prev) => (prev + 1) % answers.length);
    }, 2500);
    return () => clearInterval(interval);
  }, []);

  // 3. Animation Transforms
  const questionOpacity = useTransform(entryProgress, [0, 1], [0, 1]);
  const statementOpacity = useTransform(scrollYProgress, [0, 0.15], [0, 1]);
  
  
  // Pod & Green Card Movement
  const podOpacity = useTransform(scrollYProgress, [0.15, 0.3], [0, 1]);
  const podY = useTransform(scrollYProgress, [0.15, 0.85], [600, 0]);
  const greenCardY = useTransform(scrollYProgress, [0.4, 0.85], ["100%", "0%"]);
  const annotationsOpacity = useTransform(scrollYProgress, [0.55, 0.75], [0, 1]);

  return (
    <section ref={containerRef} className="h-[400vh] relative bg-background">
      <div className="sticky top-0 h-screen w-full flex items-center justify-center overflow-hidden">
        {/* Green Card Background Layer */}
        <motion.div 
          style={{ y: greenCardY }}
          className="absolute left-4 right-4 bottom-8 top-20 bg-primary rounded-[3rem] shadow-[0_0_60px_rgba(0,0,0,0.15)] z-0 max-w-6xl mx-auto"
        />

        <div className="container px-4 mx-auto max-w-2xl text-center z-10 relative h-full flex flex-col justify-center py-20">
          <div className="flex flex-col gap-16 relative z-10">
            {/* Question with Cycling Answer */}
            <motion.div style={{ opacity: questionOpacity }}>
              <p className="text-3xl tablet:text-4xl font-funnel font-bold text-foreground leading-relaxed">
                Ever struggled to find a quiet spot, right when you needed to…
              </p>
              <motion.div
                key={currentAnswer}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.3 }}
                className="mt-6 text-3xl tablet:text-4xl text-primary font-cormorant italic"
              >
                {answers[currentAnswer]}
              </motion.div>
            </motion.div>

            {/* Statement */}
            <motion.div style={{ opacity: statementOpacity }}>
              <p className="text-3xl tablet:text-4xl font-funnel font-bold text-foreground">
                We're making that{" "}
                <span className="text-primary">
                  easier.
                </span>
              </p>
            </motion.div>
          </div>

          {/* Pod Image Reveal */}
          <motion.div 
            style={{ opacity: podOpacity, y: podY }}
            className="absolute inset-0 flex items-end justify-center pb-20 pointer-events-none z-20"
          >
            <div className="relative w-full max-w-md mx-auto h-[70vh]">
              <img 
                src={podImage} 
                alt="Qwip Pod" 
                className="w-full h-full drop-shadow-2xl object-contain"
              />
              
              {/* Annotations Layer - Top Left */}
              <motion.div 
                style={{ opacity: annotationsOpacity }}
                className="absolute top-[15%] left-[5%] tablet:left-[10%]"
              >
                <div className="bg-card text-foreground px-4 py-2 rounded-full font-medium text-sm shadow-lg border border-border/50">
                  Fully soundproof
                </div>
              </motion.div>
              
              {/* Annotations Layer - Bottom Right */}
              <motion.div 
                style={{ opacity: annotationsOpacity }}
                className="absolute bottom-[25%] right-[5%] tablet:right-[10%]"
              >
                <div className="bg-card text-foreground px-4 py-2 rounded-full font-medium text-sm shadow-lg border border-border/50">
                  Book from £0.15/min
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default StickyQuestion;